import React from "react";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import Badge from "@components/Badge/Badge";
import Button from "@components/Button/Button";
import ErrorHandler from "@components/ErrorHandler/ErrorHandler";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import Subheader from "@components/Subheader/Subheader";
import UnauthorizedRedirection from "@components/UnauthorizedRedirection/UnauthorizedRedirection";
import { useNotification } from "@context/NotificationContext";
import { getTerminalServiceErrorMessage } from "@features/terminal-services/hooks/useTerminalServiceDocumentUpload";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { FottServiceRequestStatus, FottServiceType } from "@services/terminal-services/types";
import type {
  CloseBypassServiceRequestPayload,
  FottServiceType as FottServiceTypeValue,
  TerminalServiceDetailsResponse,
  TerminalServiceDocument,
  TerminalServiceTask,
  TerminalServiceTaskDecision,
} from "@services/terminal-services/types";
import {
  approveTerminalServiceById,
  cancelTerminalServiceById,
  claimTerminalServiceById,
  closeBypassServiceById,
  getTerminalServiceById,
  rejectTerminalServiceById,
  unclaimTerminalServiceById,
} from "@services/terminalServicesApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import BypassServiceDetails from "./components/BypassServiceDetails";
import GantryCraneServiceDetails from "./components/GantryCraneServiceDetails";
import NitrogenSupplyServiceDetails from "./components/NitrogenSupplyServiceDetails";
import PiggingServiceDetails from "./components/PiggingServiceDetails";
import ApproveTerminalServiceDrawer, {
  type TerminalServiceApproveDraft,
} from "./flow/ApproveTerminalServiceDrawer";
import CancelTerminalServiceDrawer, {
  type TerminalServiceCancelDraft,
} from "./flow/CancelTerminalServiceDrawer";
import CloseBypassServiceDrawer from "./flow/CloseBypassServiceDrawer";
import RejectTerminalServiceDrawer, {
  type TerminalServiceRejectDraft,
} from "./flow/RejectTerminalServiceDrawer";
import TerminalServiceFlowButtons from "./flow/TerminalServiceFlowButtons";
import StatusDetailsDrawer from "./StatusDetailsDrawer";
import { getTerminalServiceStatusBadgeType, normalizeFottServiceType } from "./utils";
import {
  canCancelTerminalService,
  getTerminalServiceDateForCancellation,
} from "./utils/cancellation";

const TEST_ID_BASE = "terminal-service-details";
const LANG_BASE = "terminal-services.details";

const renderServiceDetails = ({
  serviceType,
  terminalServiceDetails,
}: {
  serviceType: FottServiceTypeValue;
  terminalServiceDetails?: TerminalServiceDetailsResponse;
}) => {
  const props = { serviceType, terminalServiceDetails };

  switch (serviceType) {
    case FottServiceType.GANTRY_CRANE_SERVICE:
      return <GantryCraneServiceDetails {...props} />;
    case FottServiceType.NITROGEN_SUPPLY_SERVICE:
      return <NitrogenSupplyServiceDetails {...props} />;
    case FottServiceType.PIGGING_SERVICE:
      return <PiggingServiceDetails {...props} />;
    case FottServiceType.BYPASS_SERVICE:
      return <BypassServiceDetails {...props} />;
    default:
      return null;
  }
};

const TerminalServiceDetailsContainer = () => {
  const { i18n, t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const requestedServiceType = normalizeFottServiceType(searchParams.get("serviceType"));
  const currentUserClaimOwnerName = useAuthStore(
    (state) => state.user?.email ?? state.user?.preferredUsername ?? "",
  );
  const user = useAuthStore((state) => state.user);
  const isCustomer = user?.roles.includes("customer") ?? false;
  const isFottUser = user?.roles.includes("fott-user") ?? false;
  const [localTask, setLocalTask] = React.useState<TerminalServiceTask | null>(null);
  const [localStatus, setLocalStatus] = React.useState<string | null>(null);
  const [localTaskDecision, setLocalTaskDecision] =
    React.useState<TerminalServiceTaskDecision | null>(null);
  const [localClosureDetails, setLocalClosureDetails] = React.useState<{
    closureDate: string;
    closedBy: string;
    closureRemarks?: string;
    closureAttachment?: TerminalServiceDocument | null;
  } | null>(null);
  const [isRejectDrawerOpen, setIsRejectDrawerOpen] = React.useState(false);
  const [isApproveDrawerOpen, setIsApproveDrawerOpen] = React.useState(false);
  const [isCloseDrawerOpen, setIsCloseDrawerOpen] = React.useState(false);
  const [isCancelDrawerOpen, setIsCancelDrawerOpen] = React.useState(false);
  const [isRejectionDetailsDrawerOpen, setIsRejectionDetailsDrawerOpen] = React.useState(false);
  const [approveDrafts, setApproveDrafts] = React.useState<
    Record<string, TerminalServiceApproveDraft>
  >({});
  const [rejectDrafts, setRejectDrafts] = React.useState<
    Record<string, TerminalServiceRejectDraft>
  >({});
  const [cancelDrafts, setCancelDrafts] = React.useState<
    Record<string, TerminalServiceCancelDraft>
  >({});
  const [approveDrawerResetVersion, setApproveDrawerResetVersion] = React.useState(0);
  const [rejectDrawerResetVersion, setRejectDrawerResetVersion] = React.useState(0);
  const [closeDrawerResetVersion, setCloseDrawerResetVersion] = React.useState(0);
  const [cancelDrawerResetVersion, setCancelDrawerResetVersion] = React.useState(0);

  const isQueryEnabled = Boolean(id);

  const {
    data: terminalServiceDetails,
    isLoading,
    isError,
    error: terminalServiceError,
  } = useQuery<TerminalServiceDetailsResponse>({
    queryKey: ["terminal-service-details", id, requestedServiceType],
    queryFn: () =>
      getTerminalServiceById({
        id: id as string,
        serviceType: requestedServiceType,
      }),
    enabled: isQueryEnabled,
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  React.useEffect(() => {
    setLocalTask(null);
    setLocalStatus(null);
    setLocalTaskDecision(null);
    setLocalClosureDetails(null);
  }, [id, requestedServiceType]);

  React.useEffect(() => {
    if (!localStatus || !localTaskDecision || !terminalServiceDetails) {
      return;
    }

    const serverStatus =
      terminalServiceDetails.serviceDetails?.status ?? terminalServiceDetails.status;
    const serverDecisionStatus =
      terminalServiceDetails.serviceDetails?.taskDecision?.status ??
      (terminalServiceDetails.serviceDetails?.cancellationDetails
        ? FottServiceRequestStatus.CANCELLED
        : undefined);
    const serverHasMatchingTerminalDecision =
      serverStatus === localStatus && serverDecisionStatus === localTaskDecision.status;

    if (serverHasMatchingTerminalDecision) {
      setLocalStatus(null);
      setLocalTaskDecision(null);
    }
  }, [localStatus, localTaskDecision, terminalServiceDetails]);

  React.useEffect(() => {
    if (!localTask) {
      return;
    }

    const serverTask = terminalServiceDetails?.serviceDetails?.task;

    if (
      serverTask?.status === localTask.status &&
      (serverTask?.claimOwnerName ?? null) === (localTask.claimOwnerName ?? null)
    ) {
      setLocalTask(null);
    }
  }, [localTask, terminalServiceDetails?.serviceDetails?.task]);

  const notifyMutationError = React.useCallback(
    (mutationError: unknown) => {
      notify({
        title: t("common.error"),
        description:
          getTerminalServiceErrorMessage(mutationError) ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
    [notify, t],
  );

  const invalidateTerminalServiceQueries = React.useCallback(
    async ({
      terminalServiceId,
      serviceType,
    }: {
      terminalServiceId: string;
      serviceType: FottServiceTypeValue;
    }) => {
      await queryClient.invalidateQueries({ queryKey: ["fott-services"] });
      await queryClient.invalidateQueries({ queryKey: ["fott-services-preview"] });
      await queryClient.invalidateQueries({
        queryKey: ["terminal-service-details", id ?? terminalServiceId, serviceType],
      });
    },
    [id, queryClient],
  );

  const claimTerminalServiceMutation = useMutation({
    mutationFn: claimTerminalServiceById,
    onSuccess: async (_, variables) => {
      await invalidateTerminalServiceQueries({
        terminalServiceId: variables.id,
        serviceType: variables.serviceType,
      });
      notify({
        title: t("terminal-services.fott-services.notifications.claim-success-title"),
        type: "success",
      });
    },
    onError: notifyMutationError,
  });

  const unclaimTerminalServiceMutation = useMutation({
    mutationFn: unclaimTerminalServiceById,
    onSuccess: async (_, variables) => {
      await invalidateTerminalServiceQueries({
        terminalServiceId: variables.id,
        serviceType: variables.serviceType,
      });
      notify({
        title: t("terminal-services.fott-services.notifications.unclaim-success-title"),
        type: "success",
      });
    },
    onError: notifyMutationError,
  });

  const rejectTerminalServiceMutation = useMutation({
    mutationFn: rejectTerminalServiceById,
    onSuccess: async (_, variables) => {
      setLocalStatus("REJECTED");
      setLocalTaskDecision(variables.payload);
      setLocalTask((prevTask) =>
        prevTask
          ? {
              ...prevTask,
              status: "COMPLETED",
            }
          : prevTask,
      );
      setIsRejectDrawerOpen(false);
      setRejectDrawerResetVersion((version) => version + 1);
      setRejectDrafts((prevDrafts) => {
        const nextDrafts = { ...prevDrafts };
        delete nextDrafts[variables.id];
        return nextDrafts;
      });

      await invalidateTerminalServiceQueries({
        terminalServiceId: variables.id,
        serviceType: variables.serviceType,
      });

      notify({
        title: t(`${LANG_BASE}.notifications.reject-success-title`, {
          defaultValue: "You have rejected the FOTT Service request.",
        }),
        type: "success",
      });
    },
    onError: (error) => {
      setIsRejectDrawerOpen(false);
      notifyMutationError(error);
    },
  });

  const approveTerminalServiceMutation = useMutation({
    mutationFn: approveTerminalServiceById,
    onSuccess: async (_, variables) => {
      setLocalStatus(FottServiceRequestStatus.APPROVED);
      setLocalTaskDecision(variables.payload);
      setLocalTask((prevTask) => (prevTask ? { ...prevTask, status: "COMPLETED" } : prevTask));
      setIsApproveDrawerOpen(false);
      setApproveDrawerResetVersion((version) => version + 1);
      setApproveDrafts((current) => {
        const next = { ...current };
        delete next[variables.id];
        return next;
      });

      await invalidateTerminalServiceQueries({
        terminalServiceId: variables.id,
        serviceType: variables.serviceType,
      });
      notify({
        title: t(`${LANG_BASE}.notifications.approve-success-title`),
        type: "success",
      });
    },
    onError: (error) => {
      setIsApproveDrawerOpen(false);
      notifyMutationError(error);
    },
  });

  const cancelTerminalServiceMutation = useMutation({
    mutationFn: cancelTerminalServiceById,
    onSuccess: async (_, variables) => {
      setLocalStatus(FottServiceRequestStatus.CANCELLED);
      setLocalTaskDecision({
        status: FottServiceRequestStatus.CANCELLED,
        cancellationReason: variables.payload.reason,
        cancellationDocument: variables.payload.attachment,
        createdAt: new Date().toISOString(),
        createdBy: user?.name ?? user?.email ?? user?.preferredUsername ?? "",
      });
      setLocalTask((prevTask) => (prevTask ? { ...prevTask, status: "CANCELLED" } : prevTask));
      setIsCancelDrawerOpen(false);
      setCancelDrawerResetVersion((version) => version + 1);
      setCancelDrafts((current) => {
        const next = { ...current };
        delete next[variables.id];
        return next;
      });

      await invalidateTerminalServiceQueries({
        terminalServiceId: variables.id,
        serviceType: variables.serviceType,
      });

      notify({
        title: t(`${LANG_BASE}.notifications.cancel-success-title`, {
          defaultValue: "You have canceled this service.",
        }),
        type: "success",
      });
    },
    onError: (error) => {
      setIsCancelDrawerOpen(false);
      notifyMutationError(error);
    },
  });

  const closeBypassServiceMutation = useMutation({
    mutationFn: closeBypassServiceById,
    onSuccess: async (_, variables) => {
      const closedAt = new Date().toISOString();
      const closedBy = user?.name ?? user?.email ?? user?.preferredUsername ?? "";

      setLocalStatus(FottServiceRequestStatus.CLOSED);
      setLocalClosureDetails({
        closureDate: closedAt,
        closedBy,
        closureRemarks: variables.payload.remarks,
        closureAttachment: variables.payload.attachment,
      });
      setIsCloseDrawerOpen(false);
      setCloseDrawerResetVersion((version) => version + 1);

      await invalidateTerminalServiceQueries({
        terminalServiceId: variables.id,
        serviceType: FottServiceType.BYPASS_SERVICE,
      });

      notify({
        title: t(`${LANG_BASE}.notifications.close-success-title`),
        type: "success",
      });
    },
    onError: (error) => {
      notifyMutationError(error);
    },
  });

  const effectiveTerminalServiceDetails = React.useMemo(() => {
    if (!terminalServiceDetails?.serviceDetails) {
      return terminalServiceDetails;
    }

    return {
      ...terminalServiceDetails,
      status: localStatus ?? terminalServiceDetails.status,
      serviceDetails: {
        ...terminalServiceDetails.serviceDetails,
        status: localStatus ?? terminalServiceDetails.serviceDetails.status,
        ...(localClosureDetails
          ? {
              closureDate: localClosureDetails.closureDate,
              closedBy: localClosureDetails.closedBy,
              closureRemarks: localClosureDetails.closureRemarks,
              closureAttachment: localClosureDetails.closureAttachment,
            }
          : {}),
        task: localTask ?? terminalServiceDetails.serviceDetails.task,
        taskDecision: localTaskDecision ?? terminalServiceDetails.serviceDetails.taskDecision,
      },
    };
  }, [localClosureDetails, localStatus, localTask, localTaskDecision, terminalServiceDetails]);

  const effectiveServiceType = normalizeFottServiceType(
    effectiveTerminalServiceDetails?.serviceType
      ? String(effectiveTerminalServiceDetails.serviceType)
      : requestedServiceType,
  );
  const serviceDetails = effectiveTerminalServiceDetails?.serviceDetails;
  const serviceStatus =
    localStatus ?? serviceDetails?.status ?? effectiveTerminalServiceDetails?.status;
  const serviceTypeLabel = t(`${LANG_BASE}.service-type.${effectiveServiceType}`);
  const pageTitle = t(`${LANG_BASE}.title`);
  const statusLabel = serviceStatus ? t(`${LANG_BASE}.status-labels.${serviceStatus}`) : null;
  const terminalServiceFlowData = serviceDetails
    ? {
        id: serviceDetails.id ?? id,
        status: serviceStatus ?? null,
        task: serviceDetails.task ?? null,
      }
    : undefined;
  const currentTask = serviceDetails?.task ?? null;
  const backendCancellationDecision = serviceDetails?.cancellationDetails
    ? {
        status: FottServiceRequestStatus.CANCELLED,
        cancellationReason: serviceDetails.cancellationDetails.reason ?? null,
        cancellationDocument: serviceDetails.cancellationDetails.attachment ?? null,
        createdAt: serviceDetails.cancellationDetails.createdAt ?? null,
        createdBy: serviceDetails.cancellationDetails.createdBy ?? null,
      }
    : null;
  const effectiveTaskDecision =
    localTaskDecision ?? backendCancellationDecision ?? serviceDetails?.taskDecision ?? null;
  const shouldShowViewDetails =
    serviceStatus !== FottServiceRequestStatus.CLOSED &&
    (effectiveTaskDecision?.status === FottServiceRequestStatus.REJECTED ||
      effectiveTaskDecision?.status === FottServiceRequestStatus.APPROVED ||
      effectiveTaskDecision?.status === FottServiceRequestStatus.CANCELLED);
  const currentRejectDraft =
    terminalServiceFlowData?.id && rejectDrafts[terminalServiceFlowData.id]
      ? rejectDrafts[terminalServiceFlowData.id]
      : { notesToCustomer: "" };
  const currentCancelDraft =
    terminalServiceFlowData?.id && cancelDrafts[terminalServiceFlowData.id]
      ? cancelDrafts[terminalServiceFlowData.id]
      : { cancellationReason: "" };
  const currentApproveDraft =
    terminalServiceFlowData?.id && approveDrafts[terminalServiceFlowData.id]
      ? approveDrafts[terminalServiceFlowData.id]
      : { jobType: "", operatorName: "", notesToCustomer: "" };
  const shouldShowCloseServiceButton =
    isCustomer &&
    effectiveServiceType === FottServiceType.BYPASS_SERVICE &&
    serviceStatus === FottServiceRequestStatus.APPROVED;
  const serviceUsageData = serviceDetails as
    | {
        usageHours?: Parameters<typeof canCancelTerminalService>[0]["usageHours"];
        usageHoursFott?: Parameters<typeof canCancelTerminalService>[0]["usageHoursFott"];
      }
    | null
    | undefined;
  const shouldShowCancelServiceButton =
    isFottUser &&
    canCancelTerminalService({
      status: serviceStatus,
      serviceDate: getTerminalServiceDateForCancellation(effectiveServiceType, serviceDetails),
      serviceType: effectiveServiceType,
      usageHours: serviceUsageData?.usageHours,
      usageHoursFott: serviceUsageData?.usageHoursFott,
    });
  const handleUpdate = React.useCallback(() => {
    if (!effectiveTerminalServiceDetails) {
      return;
    }

    navigate(`/${paths.terminalServices.index}/${paths.terminalServices.requestFottService}`, {
      state: {
        mode: "update",
        terminalServiceDetails: effectiveTerminalServiceDetails,
        serviceType: effectiveServiceType,
      },
    });
  }, [effectiveServiceType, effectiveTerminalServiceDetails, navigate]);
  const handleClaim = React.useCallback(() => {
    const terminalServiceId = terminalServiceFlowData?.id;

    if (!currentTask || !terminalServiceId) {
      return;
    }

    claimTerminalServiceMutation.mutate(
      {
        id: terminalServiceId,
        serviceType: effectiveServiceType,
      },
      {
        onSuccess: () => {
          setLocalTask({
            ...currentTask,
            status: "CLAIMED",
            claimOwnerName: currentUserClaimOwnerName || currentTask.claimOwnerName,
          });
        },
      },
    );
  }, [
    claimTerminalServiceMutation,
    currentTask,
    currentUserClaimOwnerName,
    effectiveServiceType,
    terminalServiceFlowData?.id,
  ]);

  const handleUnclaim = React.useCallback(() => {
    const terminalServiceId = terminalServiceFlowData?.id;

    if (!currentTask || !terminalServiceId) {
      return;
    }

    unclaimTerminalServiceMutation.mutate(
      {
        id: terminalServiceId,
        serviceType: effectiveServiceType,
      },
      {
        onSuccess: () => {
          setLocalTask({
            ...currentTask,
            status: "READY",
            claimOwnerName: null,
          });
        },
      },
    );
  }, [
    currentTask,
    effectiveServiceType,
    terminalServiceFlowData?.id,
    unclaimTerminalServiceMutation,
  ]);

  const handleReject = React.useCallback(
    ({
      notesToCustomer,
      attachment,
    }: {
      notesToCustomer: string;
      attachment: TerminalServiceDocument | null;
    }) => {
      const terminalServiceId = terminalServiceFlowData?.id;

      if (!terminalServiceId) {
        return;
      }

      rejectTerminalServiceMutation.mutate({
        id: terminalServiceId,
        serviceType: effectiveServiceType,
        payload: {
          status: FottServiceRequestStatus.REJECTED,
          rejectionNotes: notesToCustomer,
          rejectionDocument: attachment,
          createdBy: user?.name ?? user?.email ?? user?.preferredUsername ?? "",
          createdByRole: user?.roles?.[0] ?? "",
          createdAt: new Date().toISOString(),
        },
      });
    },
    [effectiveServiceType, rejectTerminalServiceMutation, terminalServiceFlowData?.id, user],
  );

  const updateRejectDraft = React.useCallback(
    (draft: TerminalServiceRejectDraft) => {
      const terminalServiceId = terminalServiceFlowData?.id;

      if (!terminalServiceId) {
        return;
      }

      setRejectDrafts((prevDrafts) => ({
        ...prevDrafts,
        [terminalServiceId]: draft,
      }));
    },
    [terminalServiceFlowData?.id],
  );

  const handleApprove = React.useCallback(
    ({
      jobType,
      operatorName,
      notesToCustomer,
      bookingConfirmation,
    }: {
      jobType: string;
      operatorName?: string;
      notesToCustomer?: string;
      bookingConfirmation: TerminalServiceDocument;
    }) => {
      const terminalServiceId = terminalServiceFlowData?.id;
      if (!terminalServiceId) return;

      approveTerminalServiceMutation.mutate({
        id: terminalServiceId,
        serviceType: effectiveServiceType,
        payload: {
          status: FottServiceRequestStatus.APPROVED,
          jobType,
          operatorName,
          approvalNotes: notesToCustomer,
          bookingConfirmation,
          createdBy: user?.name ?? user?.email ?? user?.preferredUsername ?? "",
          createdByRole: user?.roles?.[0] ?? "",
          createdAt: new Date().toISOString(),
        },
      });
    },
    [approveTerminalServiceMutation, effectiveServiceType, terminalServiceFlowData?.id, user],
  );

  const handleCancelService = React.useCallback(
    ({
      cancellationReason,
      attachment,
    }: {
      cancellationReason: string;
      attachment: TerminalServiceDocument | null;
    }) => {
      const terminalServiceId = terminalServiceFlowData?.id;
      if (!terminalServiceId) return;

      cancelTerminalServiceMutation.mutate({
        id: terminalServiceId,
        serviceType: effectiveServiceType,
        payload: {
          reason: cancellationReason,
          attachment,
        },
      });
    },
    [cancelTerminalServiceMutation, effectiveServiceType, terminalServiceFlowData?.id, user],
  );

  const updateApproveDraft = React.useCallback(
    (draft: TerminalServiceApproveDraft) => {
      const terminalServiceId = terminalServiceFlowData?.id;
      if (!terminalServiceId) return;
      setApproveDrafts((current) => ({ ...current, [terminalServiceId]: draft }));
    },
    [terminalServiceFlowData?.id],
  );

  const closeApproveDrawer = React.useCallback(
    ({ resetDraft = false }: { resetDraft?: boolean } = {}) => {
      const terminalServiceId = terminalServiceFlowData?.id;
      setIsApproveDrawerOpen(false);
      if (resetDraft && terminalServiceId) {
        setApproveDrafts((current) => {
          const next = { ...current };
          delete next[terminalServiceId];
          return next;
        });
      }
    },
    [terminalServiceFlowData?.id],
  );

  const closeRejectDrawer = React.useCallback(
    ({ resetDraft = false }: { resetDraft?: boolean } = {}) => {
      const terminalServiceId = terminalServiceFlowData?.id;

      setIsRejectDrawerOpen(false);

      if (resetDraft && terminalServiceId) {
        setRejectDrafts((prevDrafts) => {
          const nextDrafts = { ...prevDrafts };
          delete nextDrafts[terminalServiceId];
          return nextDrafts;
        });
      }
    },
    [terminalServiceFlowData?.id],
  );

  const updateCancelDraft = React.useCallback(
    (draft: TerminalServiceCancelDraft) => {
      const terminalServiceId = terminalServiceFlowData?.id;
      if (!terminalServiceId) return;
      setCancelDrafts((current) => ({ ...current, [terminalServiceId]: draft }));
    },
    [terminalServiceFlowData?.id],
  );

  const closeCancelDrawer = React.useCallback(
    ({ resetDraft = false }: { resetDraft?: boolean } = {}) => {
      const terminalServiceId = terminalServiceFlowData?.id;

      setIsCancelDrawerOpen(false);

      if (resetDraft && terminalServiceId) {
        setCancelDrafts((current) => {
          const next = { ...current };
          delete next[terminalServiceId];
          return next;
        });
      }
    },
    [terminalServiceFlowData?.id],
  );

  const handleCloseBypassService = React.useCallback(
    (payload: CloseBypassServiceRequestPayload) => {
      const terminalServiceId = terminalServiceFlowData?.id ?? id;

      if (!terminalServiceId) {
        return;
      }

      closeBypassServiceMutation.mutate({
        id: terminalServiceId,
        payload,
      });
    },
    [closeBypassServiceMutation, id, terminalServiceFlowData?.id],
  );

  if (!id) {
    return null;
  }
  if (terminalServiceError) {
    return <UnauthorizedRedirection />;
  }

  return (
    <PageLayout dir={dir}>
      <Subheader
        testId={`${TEST_ID_BASE}-subheader`}
        subtitle={
          <div
            className="flex items-center space-x-2 text-sm text-gray-700"
            data-testid={`${TEST_ID_BASE}-breadcrumb`}
          >
            <Link
              to={`/${paths.terminalServices.index}/${paths.terminalServices.fottServices}`}
              className="text-blue-600 hover:underline"
              data-testid={`${TEST_ID_BASE}-breadcrumb-link`}
            >
              {t("terminal-services.fott-services.title")}
            </Link>
            <span className="text-gray-400">/</span>
            <span data-testid={`${TEST_ID_BASE}-breadcrumb-current`}>{serviceTypeLabel}</span>
          </div>
        }
        title={
          <div className="flex flex-col gap-4 w-full md:flex-row md:items-end md:justify-between">
            <div>
              <span data-testid={`${TEST_ID_BASE}-title`}>{pageTitle}</span>
              {statusLabel && (
                <div className="mt-1 flex items-center">
                  <Badge
                    testId={`${TEST_ID_BASE}-status`}
                    type={getTerminalServiceStatusBadgeType(serviceStatus)}
                    label={statusLabel}
                    bold={false}
                  />
                  {shouldShowViewDetails && (
                    <>
                      <span className="mx-1 text-gray-500 font-normal">-</span>
                      <Link
                        to="#"
                        className="text-sm text-blue-800 hover:underline"
                        onClick={(event) => {
                          event.preventDefault();
                          setIsRejectionDetailsDrawerOpen(true);
                        }}
                        data-testid={`${TEST_ID_BASE}-view-details-link`}
                      >
                        {t("common.view-details")}
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
            <TerminalServiceFlowButtons
              terminalServiceFlowData={terminalServiceFlowData}
              onClaim={handleClaim}
              onUnclaim={handleUnclaim}
              onApprove={() => setIsApproveDrawerOpen(true)}
              onReject={() => setIsRejectDrawerOpen(true)}
              onUpdate={handleUpdate}
            />
            {shouldShowCloseServiceButton && (
              <Button
                variant="outlined"
                startIcon={<HighlightOffOutlinedIcon />}
                label={<span className="normal-case">{t(`${LANG_BASE}.close-button`)}</span>}
                onClick={() => setIsCloseDrawerOpen(true)}
                testId={`${TEST_ID_BASE}-close-service-button`}
              />
            )}
            {shouldShowCancelServiceButton && (
              <Button
                variant="outlined"
                startIcon={<HighlightOffOutlinedIcon />}
                label={<span className="normal-case">{t(`${LANG_BASE}.cancel-button`)}</span>}
                onClick={() => setIsCancelDrawerOpen(true)}
                testId="cancel"
              />
            )}
          </div>
        }
      />

      <div data-testid={TEST_ID_BASE} className="flex flex-col w-full h-full p-8 px-24">
        {isLoading && (
          <div className="relative min-h-[240px]" data-testid={`${TEST_ID_BASE}-loading`}>
            <LoadingOverlay loading={isLoading} />
          </div>
        )}

        {isError && (
          <ErrorHandler error={terminalServiceError} testId={`${TEST_ID_BASE}-error-handler`} />
        )}

        {!isLoading &&
          !isError &&
          renderServiceDetails({
            serviceType: effectiveServiceType,
            terminalServiceDetails: effectiveTerminalServiceDetails,
          })}
      </div>
      <RejectTerminalServiceDrawer
        key={`reject-${terminalServiceFlowData?.id ?? id}-${rejectDrawerResetVersion}`}
        isOpen={isRejectDrawerOpen}
        requestId={terminalServiceFlowData?.id ?? id}
        draft={currentRejectDraft}
        onDraftChange={updateRejectDraft}
        onClose={closeRejectDrawer}
        onReject={handleReject}
        isSubmitting={rejectTerminalServiceMutation.isPending}
      />
      <ApproveTerminalServiceDrawer
        key={`approve-${terminalServiceFlowData?.id ?? id}-${approveDrawerResetVersion}`}
        isOpen={isApproveDrawerOpen}
        requestId={terminalServiceFlowData?.id ?? id}
        serviceType={effectiveServiceType}
        draft={currentApproveDraft}
        onDraftChange={updateApproveDraft}
        onClose={closeApproveDrawer}
        onApprove={handleApprove}
        isSubmitting={approveTerminalServiceMutation.isPending}
      />
      <StatusDetailsDrawer
        isOpen={isRejectionDetailsDrawerOpen}
        onClose={() => setIsRejectionDetailsDrawerOpen(false)}
        taskDecision={effectiveTaskDecision}
      />
      {isCancelDrawerOpen && (
        <CancelTerminalServiceDrawer
          key={`cancel-${terminalServiceFlowData?.id ?? id}-${cancelDrawerResetVersion}`}
          isOpen={isCancelDrawerOpen}
          requestId={terminalServiceFlowData?.id ?? id}
          draft={currentCancelDraft}
          onDraftChange={updateCancelDraft}
          onClose={closeCancelDrawer}
          onCancelService={handleCancelService}
          isSubmitting={cancelTerminalServiceMutation.isPending}
        />
      )}
      <CloseBypassServiceDrawer
        key={`close-${terminalServiceFlowData?.id ?? id}-${closeDrawerResetVersion}`}
        isOpen={isCloseDrawerOpen}
        requestId={terminalServiceFlowData?.id ?? id}
        onClose={() => setIsCloseDrawerOpen(false)}
        onCloseService={handleCloseBypassService}
        isSubmitting={closeBypassServiceMutation.isPending}
      />
    </PageLayout>
  );
};

export default TerminalServiceDetailsContainer;
